<template>
  <div class="h-screen bg-secondary overflow-auto">
    <VBusHelper :emitOnMounted="$constants.EVENTS.LEADERBOARD.LIST_ALL.REFRESH" />

    <!-- Loading state -->
    <div v-if="isLoading" class="h-full flex flex-col items-center justify-center text-white">
      <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-white mb-4"></div>
      <div class="text-lg font-medium">Chargement du classement...</div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="h-full flex flex-col items-center justify-center text-white px-6">
      <div class="text-6xl mb-4">⚠️</div>
      <div class="text-xl font-semibold mb-2">Erreur de chargement</div>
      <div class="text-white/70 text-center mb-6">{{ error }}</div>
      <button
        @click="proxy.$bus.emit(proxy.$constants.EVENTS.LEADERBOARD.LIST_ALL.REFRESH)"
        class="px-6 py-3 bg-white/20 backdrop-blur border border-white/30 rounded-2xl font-semibold hover:bg-white/30 transition-all"
      >
        Réessayer
      </button>
    </div>

    <!-- Main content -->
    <div v-else>
      <div class="py-4"></div>
      <div class="text-white sticky top-1">
        <div class="mobile-status-padding"></div>

        <div class="text-center text-3xl font-bold tracking-wide">Classements</div>
      </div>
      <div class="px-6 py-2 text-white/70 text-center text-sm">
        De grandes surprises se préparent !
      </div>
      
      <!-- Bientot disponible state -->
      <div class="flex flex-col items-center justify-center p-6 mt-10">
        <div class="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 text-center shadow-2xl relative overflow-hidden">
          <div class="absolute -top-10 -right-10 w-32 h-32 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob"></div>
          <div class="absolute -bottom-10 -left-10 w-32 h-32 bg-yellow-400 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-2000"></div>
          
          <div class="text-7xl mb-6 relative z-10 drop-shadow-lg">✨</div>
          <h2 class="text-2xl font-bold text-white mb-3 relative z-10">Le Classement Arrive !</h2>
          <p class="text-white/80 text-sm leading-relaxed mb-6 font-medium relative z-10">
            Nous travaillons sur une toute nouvelle expérience pour vous récompenser. <br><br>Gagnez des bonus, grimpez dans le classement et débloquez des avantages exclusifs très prochainement.
          </p>
          
          <div class="inline-flex items-center justify-center space-x-2 bg-white/20 px-6 py-3 rounded-full text-white font-semibold text-sm shadow-inner relative z-10">
            <div class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span>Bientôt disponible</span>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import podiumCenterImage from '@/assets/images/leaderboard/podium-center.svg';
import podiumLeftImage from '@/assets/images/leaderboard/podium-left.svg';
import cardHeadForm from '@/assets/images/leaderboard/cardHeadForm.svg';
import { ImgUtils } from '@/app/ui/core/img/imgUtils.js';

const { proxy } = getCurrentInstance();

// États réactifs
const currentTab = ref('weekly');
const timeRemaining = ref('0j 30h 0min');

// Store data
const leaderboardData = computed(() => proxy.$stores.leaderboard.state.list_all);
const isLoading = computed(() => leaderboardData.value.loading);
const error = computed(() => leaderboardData.value.error);

// Computed properties pour les données actuelles (déjà formatées par le serializer)
const currentData = computed(() => {
  return currentTab.value === 'weekly'
    ? leaderboardData.value.weekly
    : leaderboardData.value.alltime;
});

const topThree = computed(() => currentData.value.topThree || []);
const otherPlayers = computed(() => currentData.value.otherPlayers || []);
const currentUser = computed(() => currentData.value.currentUser);

// Méthodes
const formatScore = (score) => {
  return `${parseInt(score).toLocaleString('fr-FR')} FCFA`;
};

const formatBonus = (bonus) => {
  return `${parseInt(bonus).toLocaleString('fr-FR')} FCFA`;
};

const getUserRankMessage = () => {
  if (!currentUser.value) return '';
  const percentage = Math.round((1 - currentUser.value.position / 100) * 100);
  return `Vous faites mieux que ${percentage}% des autres joueurs!`;
};

const switchTab = (tab) => {
  if (tab !== currentTab.value && !isLoading.value) {
    currentTab.value = tab;
  }
};

const updateTimeRemaining = () => {
  const now = new Date();
  const endOfWeek = new Date(now);
  endOfWeek.setDate(now.getDate() + (7 - now.getDay()));
  endOfWeek.setHours(23, 59, 59, 999);

  const diff = endOfWeek - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  timeRemaining.value = `${days}j ${hours}h ${minutes}min`;
};

// Lifecycle hooks
onMounted(() => {
  updateTimeRemaining();

  // Mettre à jour le temps restant toutes les minutes
  setInterval(updateTimeRemaining, 60000);
});
</script>
